import { useEffect, useState } from "react";
import { X, Link2, Copy, Check, Lock, Trash2, Eye, Loader2 } from "lucide-react";
import { createShare, listShares, revokeShare } from "../lib/api";

// "Bagikan": turn the report on screen into a public link. The link carries a
// SNAPSHOT (buildPayload() — the same data the page renders and prints from), so
// the recipient keeps seeing exactly what was shared. Optional password, expiry
// (30 days by default, or none), and the list of this report's live links with
// their view counts and a revoke button.

const EXPIRY = [["7", "7 hari"], ["30", "30 hari"], ["90", "90 hari"], ["", "Tanpa batas"]];
const fmtDate = (iso) => new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" });

export default function ShareModal({ open, onClose, kind, gridId, storeId, title, ownerLabel, buildPayload }) {
  const [shares, setShares]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [expires, setExpires] = useState("30");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied]   = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    if (!open) return;
    setCreated(null); setErr(null); setCopied(false); setPassword("");
    setLoading(true);
    listShares({ kind, gridId, storeId }).then(setShares).catch(() => setShares([])).finally(() => setLoading(false));
  }, [open, kind, gridId, storeId]);

  if (!open) return null;

  const urlOf = (s) => `${window.location.origin}${s.path}`;
  const copy = async (s) => {
    try { await navigator.clipboard.writeText(urlOf(s)); setCopied(s.id); setTimeout(() => setCopied(false), 1800); }
    catch { /* clipboard blocked — the URL is visible to select */ }
  };

  const create = async () => {
    setCreating(true); setErr(null);
    try {
      const s = await createShare({
        kind, gridId, storeId, title, ownerLabel,
        payload: buildPayload(),
        password: password || null,
        expiresDays: expires === "" ? null : Number(expires),
      });
      setCreated(s);
      setShares((l) => [s, ...l]);
      copy(s);
    } catch (e) {
      setErr(e.message);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (s) => {
    try {
      await revokeShare(s.id);
      setShares((l) => l.filter((x) => x.id !== s.id));
      if (created?.id === s.id) setCreated(null);
    } catch (e) { setErr(e.message); }
  };

  const live = shares.filter((s) => !s.revoked && !s.expired);

  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
      onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-slate-100">
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><Link2 className="w-4 h-4 text-brand-700" /> Bagikan laporan</p>
            <p className="text-[11px] text-slate-400 mt-0.5 truncate">{title}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {created ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 mb-2">Tautan dibuat{copied === created.id ? " · tersalin" : ""}</p>
              <div className="flex items-center gap-2">
                <input readOnly value={urlOf(created)} onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-0 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-mono text-slate-700" />
                <button onClick={() => copy(created)} className="shrink-0 rounded-lg bg-brand-700 hover:bg-brand-800 px-3 py-2 text-xs font-bold text-white flex items-center gap-1.5">
                  {copied === created.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Salin
                </button>
              </div>
              <p className="text-[11px] text-emerald-700 mt-2">
                {created.hasPassword ? "Dilindungi kata sandi — kirim kata sandinya terpisah. " : "Siapa pun yang punya tautan ini bisa membukanya. "}
                {created.expiresAt ? `Berlaku sampai ${fmtDate(created.expiresAt)}.` : "Tanpa batas waktu."}
              </p>
              <button onClick={() => setCreated(null)} className="mt-2 text-[11px] font-semibold text-emerald-700 hover:underline">Buat tautan lain</button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] font-semibold text-slate-500">Kata sandi (opsional)</span>
                  <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Kosongkan bila tidak perlu"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-brand-500" />
                </label>
                <label className="block">
                  <span className="text-[11px] font-semibold text-slate-500">Masa berlaku</span>
                  <select value={expires} onChange={(e) => setExpires(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:border-brand-500">
                    {EXPIRY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Penerima melihat laporan ini persis seperti sekarang, termasuk profil bisnis Anda, tanpa perlu akun Landhep. Pembaruan laporan di kemudian hari tidak mengubah isi tautan.
              </p>
              {err && <p className="text-xs text-red-600">{err}</p>}
              <button onClick={create} disabled={creating}
                className="w-full rounded-xl bg-brand-700 hover:bg-brand-800 disabled:opacity-50 px-4 py-2.5 text-sm font-bold text-white flex items-center justify-center gap-2">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Buat tautan
              </button>
            </div>
          )}

          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Tautan aktif untuk laporan ini</p>
            {loading ? <p className="text-xs text-slate-400">Memuat…</p>
              : live.length === 0 ? <p className="text-xs text-slate-400">Belum ada tautan.</p>
              : (
                <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100">
                  {live.map((s) => (
                    <li key={s.id} className="flex items-center gap-3 px-3 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-mono text-slate-700 truncate">{urlOf(s)}</p>
                        <p className="text-[10px] text-slate-400 flex items-center gap-2 mt-0.5">
                          {s.hasPassword && <span className="inline-flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" /> sandi</span>}
                          <span className="inline-flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" /> {s.viewCount}× dibuka</span>
                          <span>{s.expiresAt ? `sampai ${fmtDate(s.expiresAt)}` : "tanpa batas"}</span>
                        </p>
                      </div>
                      <button onClick={() => copy(s)} title="Salin tautan" className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-brand-700 hover:bg-slate-50">
                        {copied === s.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => revoke(s)} title="Cabut tautan" className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
